import { type FC, useCallback, useMemo } from "react";
import { ArrowRight, Check, Info } from "lucide-react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";

import { Button } from "@/shared/components/ui/button";
import { OptionCard } from "@/shared/components/ui/OptionCard";
import { Tag } from "@/shared/components/ui/tag";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import { ASSET_FIELD_CONFIG } from "./AddOtherAsset";

interface ChecklistItem {
  value: string;
  label: string;
  tooltip?: string;
}

interface ChecklistCategory {
  title: string;
  items: ChecklistItem[];
}

function buildChecklistCategories(firstName: string): ChecklistCategory[] {
  return [
    {
      title: "Receivables",
      items: [
        { value: "loans_made", label: "Loans made to someone else" },
        {
          value: "final_paychecks",
          label: "Final paychecks or unpaid wages",
          tooltip: `Pay ${firstName} earned from a job but hadn't received yet, like a final paycheck or unused vacation time.`,
        },
        {
          value: "refunds_in_process",
          label: "Refunds in process",
          tooltip: `A refund ${firstName} was owed but hadn't received yet, like from taxes, insurance, or a utility deposit.`,
        },
        { value: "legal_settlements", label: "Legal settlements" },
        { value: "ltc_benefits_owed", label: "Long-term care benefits owed" },
      ],
    },
    {
      title: "Work-related pay and shares",
      items: [
        { value: "vested_stock_options", label: "Vested stock options (ISOs / NSOs)" },
        { value: "restricted_stock_units", label: "Restricted stock units" },
        {
          value: "deferred_compensation",
          label: "Deferred compensation plans",
          tooltip: `Pay ${firstName} earned through work but agreed to receive later, often after retirement.`,
        },
        {
          value: "executive_retirement",
          label: "Executive retirement plans",
          tooltip:
            "A special retirement plan some employers set up for senior leaders, on top of a regular 401(k).",
        },
      ],
    },
    {
      title: "Royalties and Intellectual Property",
      items: [
        { value: "patents", label: "Patents" },
        { value: "book_music_royalties", label: "Books or music royalties" },
        { value: "trademark_licensing", label: "Trademark or licensing deal" },
      ],
    },
    {
      title: "Prepaid Services and Credits",
      items: [
        { value: "prepaid_funeral", label: "Prepaid funeral plan" },
        { value: "gift_cards", label: "Gift cards and store credits" },
        { value: "airline_miles", label: "Airline miles and loyalty points" },
      ],
    },
  ];
}

const NONE_VALUE = "none_of_the_above";

const ALL_CHECKLIST_KEYS = buildChecklistCategories("").flatMap((cat) =>
  cat.items.map((i) => i.value),
);

export const OtherAssetsChecklist: FC<FlowCustomComponentProps> = ({
  onComplete,
  answers,
  setAnswer,
}) => {
  const { person } = useAppContext();
  const firstName = person?.first_name ?? "they";
  const estateId = person?.estate_id;
  const { assets } = useEstateItems(estateId);
  const categories = useMemo(() => buildChecklistCategories(firstName), [firstName]);
  const rawSelected = answers["other_assets_selected"];
  const rawArray: string[] = Array.isArray(rawSelected) ? rawSelected : [];

  const addedKeys = useMemo(() => {
    const existingItemTypes = new Set(assets.map((item) => item.item_type));
    const keys = new Set<string>();
    for (const [key, config] of Object.entries(ASSET_FIELD_CONFIG)) {
      if (existingItemTypes.has(config.itemType)) keys.add(key);
    }
    return keys;
  }, [assets]);

  const selected = useMemo(() => {
    const merged = new Set([...rawArray, ...addedKeys]);
    if (rawArray.includes(NONE_VALUE)) merged.add(NONE_VALUE);
    return [...merged];
  }, [rawArray, addedKeys]);

  const isNoneSelected = selected.includes(NONE_VALUE);

  const toggle = useCallback(
    (value: string) => {
      if (value === NONE_VALUE) {
        if (isNoneSelected) {
          setAnswer("other_assets_selected", [...addedKeys]);
        } else {
          setAnswer("other_assets_selected", [...addedKeys, NONE_VALUE]);
        }
        return;
      }
      if (addedKeys.has(value)) return;
      const next = new Set(selected);
      next.delete(NONE_VALUE);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      setAnswer("other_assets_selected", [...next]);
    },
    [selected, isNoneSelected, addedKeys, setAnswer],
  );

  const hasSelection = selected.length > 0;

  return (
    <div className="flex flex-col gap-8">
      {categories.map((category) => (
        <div key={category.title} className="flex flex-col gap-3">
          <p className="text-sm font-semibold">{category.title}</p>
          {category.items.map((item) => {
            const isAdded = addedKeys.has(item.value);
            const isDisabled = isNoneSelected && !isAdded;
            return (
              <div key={item.value} className="relative">
                <OptionCard
                  label={item.label}
                  selected={isAdded || (!isNoneSelected && selected.includes(item.value))}
                  onClick={() => !isDisabled && !isAdded && toggle(item.value)}
                  variant="checkbox"
                  className={
                    isDisabled
                      ? "opacity-50 pointer-events-none"
                      : isAdded
                        ? "pointer-events-none"
                        : ""
                  }
                />
                {isAdded && (
                  <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
                    <Tag color="green" icon={Check} iconStroke={2.5}>
                      Added
                    </Tag>
                  </div>
                )}
                {!isAdded && item.tooltip && (
                  <div className="pointer-events-auto absolute right-4 top-1/2 -translate-y-1/2">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-tertiary" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">{item.tooltip}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <div className="flex items-center gap-3 py-1">
        <div className="h-px flex-1 bg-border-primary" />
        <span className="text-sm text-secondary">OR</span>
        <div className="h-px flex-1 bg-border-primary" />
      </div>

      <OptionCard
        label="None of the above"
        selected={isNoneSelected}
        onClick={() => toggle(NONE_VALUE)}
        variant="checkbox"
      />

      <div className="flex flex-col items-center gap-3">
        <Button
          variant="brand"
          rounded
          className="w-[400px]"
          onClick={() => {
            setAnswer("other_assets_selected", selected);
            onComplete();
          }}
          disabled={!hasSelection}
        >
          Continue <ArrowRight className="h-4 w-4" />
        </Button>
        <Button
          variant="link"
          className="text-secondary"
          onClick={() => {
            setAnswer("other_assets_selected", [...addedKeys, NONE_VALUE]);
            onComplete();
          }}
        >
          Skip for now
        </Button>
      </div>
    </div>
  );
};

export { ALL_CHECKLIST_KEYS, NONE_VALUE };
